// nav-data-loader.js — Centralized NAV data loading utility
// Shared by Markets tab and DSV Simulator tab.
// No React dependencies. Importable by any JS module.

import { convertApyData, DATA_NAME_ALIASES } from './dsv-engine';

// ── Raw data imports ──
import rawAcredDaily from '../data/ACRED_daily_nav_90d.json';
import rawMfOneNav from '../data/mF-ONE_NAV_History.json';
import rawGdadfNav from '../data/fasanara_gdadf_nav_history.json';
import rawFalconxNav from '../data/falconx-nav-interpolated.json';
import rawReusdNav from '../data/reusd_nav_history.json';
import rawSusdaiNav from '../data/susdai_nav_history.json';
import rawSusdsNav from '../data/susds_nav_history.json';
import rawSnusdNav from '../data/snusd_nav_history.json';
import rawSavusdNav from '../data/savusd_nav_history.json';
import rawStrcNav from '../data/strc_total_return_history.json';
import rawMorphoNav from '../data/morpho_vaults_nav_history.json';
import rawYieldData from '../data/yield-data-90day.json';
import royethRateData from '../data/royeth-rate-data-prime.json';

export { royethRateData };

// ── NAV source definitions ──
// Each entry extracts records from raw JSON, normalizes to base-1 NAV,
// and sorts by date ascending.

/**
 * Extract a { dates, navs } series from a raw NAV array or records list.
 * Normalizes NAV values to base-1 (first value = 1.0).
 *
 * @param {Array} records  - Array of row objects
 * @param {string} navField - Property name for the NAV value
 * @returns {{ dates: string[], navs: number[] } | null}
 */
function extractNav(records, navField) {
  if (!records || records.length < 2) return null;
  // Sort by date ascending (defensive — most files are pre-sorted)
  const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
  const baseNav = sorted[0][navField];
  if (!baseNav || baseNav === 0) return null;
  return {
    dates: sorted.map(d => d.date),
    navs: sorted.map(d => d[navField] / baseNav),
  };
}

// ── High-confidence NAV sources (real on-chain / oracle NAV data) ──

const NAV_SOURCE_DEFS = [
  {
    name: 'Apollo ACRED (Securitize)',
    build: () => extractNav(rawAcredDaily.data, 'nav_value_usd'),
  },
  {
    name: 'Fasanara mf-ONE',
    build: () => extractNav(
      rawMfOneNav.records,
      'nav_price',
    ),
  },
  {
    name: 'Fasanara GDADF',
    build: () => extractNav(rawGdadfNav.records, 'nav_price'),
  },
  {
    name: 'Pareto FalconX',
    build: () => {
      // FalconX data is already base-1 normalized
      if (!rawFalconxNav || rawFalconxNav.length < 2) return null;
      const sorted = [...rawFalconxNav].sort((a, b) => a.date.localeCompare(b.date));
      return {
        dates: sorted.map(d => d.date),
        navs: sorted.map(d => d.nav),
      };
    },
  },
  {
    name: 'Re Protocol reUSD',
    build: () => extractNav(rawReusdNav.records, 'nav_price'),
  },
  {
    name: 'sUSDai',
    build: () => extractNav(rawSusdaiNav.records, 'nav_price'),
  },
  {
    name: 'Reserve Sky sUSDS',
    build: () => extractNav(rawSusdsNav.records, 'nav_price'),
  },
  {
    name: 'Neutrl sNUSD',
    build: () => extractNav(rawSnusdNav.records, 'nav_price'),
  },
  {
    name: 'Avant savUSD',
    build: () => extractNav(rawSavusdNav.records, 'nav_price'),
  },
  {
    name: 'Strategy Inc STRC',
    build: () => extractNav(rawStrcNav.records, 'nav_price'),
  },
  // Morpho MetaMorpho vaults — real share price from historicalState.sharePrice
  // Apply DATA_NAME_ALIASES so navCache keys match the engine strategy names used in market presets
  ...Object.entries(rawMorphoNav.vaults).map(([name, vault]) => ({
    name: DATA_NAME_ALIASES[name] || name,
    build: () => extractNav(vault.records, 'nav_price'),
  })),
];

// ── Build caches (computed once on first import) ──

/** @type {Map<string, { dates: string[], navs: number[], source: 'nav' }>} */
const navCache = new Map();
for (const def of NAV_SOURCE_DEFS) {
  const result = def.build();
  if (result) {
    navCache.set(def.name, { ...result, source: 'nav' });
  }
}

/** @type {Map<string, { dates: string[], navs: number[], source: 'apy' }>} */
const apyCache = new Map();
const apyConverted = convertApyData(rawYieldData);
for (const [name, series] of Object.entries(apyConverted)) {
  if (series && series.dates && series.dates.length >= 2) {
    // convertApyData already returns sorted data
    apyCache.set(name, { ...series, source: 'apy' });
  }
}

// ── Public API ──

/**
 * Get NAV timeseries for a strategy by name.
 * NAV sources take priority over APY-derived sources when both exist.
 *
 * @param {string} name - Strategy name (must match dsv-engine DEFAULT_STRATEGIES names)
 * @returns {{ dates: string[], navs: number[], source: 'nav'|'apy' } | null}
 */
export function getNavData(name) {
  return navCache.get(name) || apyCache.get(name) || null;
}

/**
 * List all available strategies with their data source type.
 * NAV sources are listed first, then APY sources.
 *
 * @returns {Array<{ name: string, source: 'nav'|'apy' }>}
 */
export function listAvailableData() {
  const result = [];
  for (const [name] of navCache) {
    result.push({ name, source: 'nav' });
  }
  for (const [name] of apyCache) {
    // Skip APY entries that are already covered by a NAV source
    if (!navCache.has(name)) {
      result.push({ name, source: 'apy' });
    }
  }
  return result;
}

/**
 * Convert { dates, navs } format to [{ date, nav }] timeseries array.
 * Useful for market-engine and chart components that expect row-based data.
 *
 * @param {{ dates: string[], navs: number[] }} series
 * @returns {Array<{ date: string, nav: number }>}
 */
export function toTimeseries({ dates, navs }) {
  if (!dates || !navs) return [];
  return dates.map((date, i) => ({ date, nav: navs[i] }));
}

/**
 * Get all NAV + APY data as a single object keyed by strategy name.
 * Returns the same shape as DsvSimulator's builtinNavData useMemo.
 * NAV sources override APY sources when both exist for the same name.
 *
 * @returns {Object<string, { dates: string[], navs: number[] }>}
 */
export function getAllNavData() {
  const result = {};
  // Start with APY data
  for (const [name, series] of apyCache) {
    result[name] = { dates: series.dates, navs: series.navs };
  }
  // Overlay NAV data (higher confidence, takes priority)
  for (const [name, series] of navCache) {
    result[name] = { dates: series.dates, navs: series.navs };
  }
  return result;
}
